// Навигатор по длинной статье: тёмная «таблетка», прилипающая под шапкой. В ней кольцо
// прогресса и текущий раздел — они меняются при прокрутке. Нажатие раскрывает список
// всех разделов поверх текста; закрывается повторным нажатием, Esc, кликом мимо или
// выбором раздела. Строится из H2 текста (id от Ghost), если разделов не меньше
// MIN_SECTIONS. Где стоит таблетка (слева от текста или над ним), решает CSS.
// Вызывается из pageInit() на каждой странице (PJAX, bfcache) и пересобирается с нуля.

const MIN_SECTIONS = 4;
// Раздел текущий, если его заголовок выше этой линии: шапка + треть оставшегося окна.
const ACTIVE_LINE_SHARE = 1 / 3;
const PANEL_ID = "th-toc-panel";

let sections = [];
let navigator = null;
let scheduled = false;
let isTrackingStarted = false;

export default function toc() {
    startTracking();
    sections = [];
    navigator = null;
    const body = document.querySelector(".th-post-body");
    if (!body) return;
    body.querySelectorAll(".th-toc").forEach((old) => old.remove());
    const headings = [...body.querySelectorAll(".gh-content > h2[id]")];
    if (headings.length < MIN_SECTIONS) return;

    navigator = buildNavigator(headings);
    body.prepend(navigator.root);
    sections = headings.map((heading) => ({
        heading,
        link: navigator.root.querySelector(`[data-target="${CSS.escape(heading.id)}"]`),
    }));
    // Сразу показываем первый раздел, а настоящий текущий (якорь, возврат назад) меряем
    // после отрисовки кадра, когда раскладка уже готова. Любой замер прямо здесь — даже
    // scrollY — заставил бы браузер пересчитать раскладку всей статьи внутри стартового скрипта.
    renderCurrent(0);
    afterPaint(highlightCurrent);
}

/** Прокручивает к элементу из #якоря адреса, если он есть (с отступом scroll-margin). */
function realignAnchor() {
    const id = location.hash.slice(1);
    if (!id) return;
    let target = null;
    try {
        target = document.getElementById(decodeURIComponent(id));
    } catch {
        return;
    }
    if (target) target.scrollIntoView();
}

/** Вызывает fn после ближайшей отрисовки кадра — раскладка к этому моменту уже посчитана. */
function afterPaint(fn) {
    requestAnimationFrame(() => setTimeout(fn, 0));
}

/** Один раз на сессию: высота шапки, подсветка по прокрутке, закрытие списка. */
function startTracking() {
    if (isTrackingStarted) return;
    isTrackingStarted = true;
    const topbar = document.querySelector(".th-topbar");
    const setTopbarHeight = (height) => {
        document.documentElement.style.setProperty("--th-topbar-h", `${Math.round(height)}px`);
        scheduleHighlight();
    };
    // ResizeObserver сам сообщает размер после раскладки браузера; берём его из записи,
    // а не из offsetHeight — другой наблюдатель мог уже поменять страницу. Синхронный
    // замер — только там, где наблюдателя нет.
    if (topbar && "ResizeObserver" in window) {
        let isFirstReport = true;
        new ResizeObserver(([entry]) => {
            setTopbarHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
            // Браузер прокрутил к #якорю раньше, чем узнал высоту шапки, — заголовок
            // уехал под неё. Теперь отступ известен: доводим ещё раз.
            if (isFirstReport) realignAnchor();
            isFirstReport = false;
        }).observe(topbar);
    } else {
        setTopbarHeight(topbar ? topbar.offsetHeight : 0);
    }
    window.addEventListener("scroll", scheduleHighlight, { passive: true });
    window.addEventListener("resize", scheduleHighlight, { passive: true });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && isOpen()) {
            setOpen(false);
            navigator.toggle.focus();
        }
    });
    document.addEventListener("click", (event) => {
        if (isOpen() && !navigator.root.contains(event.target)) setOpen(false);
    });
}

function buildNavigator(headings) {
    const root = element("nav", "th-toc");
    root.setAttribute("aria-label", "On this page");
    const bar = element("div", "th-toc__bar");

    const toggle = element("button", "th-toc__toggle");
    toggle.type = "button";
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-controls", PANEL_ID);
    const ring = element("span", "th-toc__ring");
    ring.setAttribute("aria-hidden", "true");
    const current = element("span", "th-toc__current");
    const chevron = element("span", "th-toc__chevron");
    chevron.append(icon('<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>', 16));
    toggle.append(ring, current, chevron);
    toggle.addEventListener("click", () => setOpen(!isOpen()));

    const panel = element("div", "th-toc__panel");
    panel.id = PANEL_ID;
    panel.hidden = true;
    const title = element("p", "th-toc__title");
    // Текущий раздел прибит к верху прокручиваемого списка — виден всегда.
    const pinned = element("a", "th-toc__pinned");
    pinned.addEventListener("click", () => setOpen(false));
    const pinnedText = element("span");
    pinned.append(icon('<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>', 14), pinnedText);
    const list = element("ol", "th-toc__list");
    headings.forEach((heading) => {
        const link = element("a", "th-toc__link");
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.dataset.target = heading.id;
        link.textContent = heading.textContent.trim();
        link.addEventListener("click", () => setOpen(false));
        const item = element("li");
        item.append(link);
        list.append(item);
    });
    panel.append(title, pinned, list);
    bar.append(toggle, panel);
    root.append(bar);
    return { root, toggle, panel, ring, current, title, pinned, pinnedText, total: headings.length };
}

function isOpen() {
    return Boolean(navigator) && navigator.toggle.getAttribute("aria-expanded") === "true";
}

function setOpen(open) {
    if (!navigator) return;
    navigator.toggle.setAttribute("aria-expanded", String(open));
    navigator.panel.hidden = !open;
    if (open) scrollListToCurrent();
}

/** Прокручивает открытый список так, чтобы текущий раздел был сразу под закреплённой строкой. */
function scrollListToCurrent() {
    const link = navigator.panel.querySelector(".th-toc__link[aria-current]");
    if (!link) return;
    const offset = navigator.title.offsetHeight + navigator.pinned.offsetHeight;
    navigator.panel.scrollTop = Math.max(0, link.offsetTop - offset);
}

function scheduleHighlight() {
    if (scheduled || sections.length === 0) return;
    scheduled = true;
    requestAnimationFrame(() => {
        scheduled = false;
        highlightCurrent();
    });
}

/** Текущий раздел — последний, чей заголовок уже выше активной линии. */
function highlightCurrent() {
    if (sections.length === 0) return;
    const topbar = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--th-topbar-h"),
    ) || 0;
    const line = topbar + (window.innerHeight - topbar) * ACTIVE_LINE_SHARE;
    let index = 0;
    sections.forEach((section, position) => {
        if (section.heading.getBoundingClientRect().top <= line) index = position;
    });
    renderCurrent(index);
}

/** Показывает раздел под номером index (с нуля): таблетка, кольцо, список, подписи. */
function renderCurrent(index) {
    sections.forEach((section, position) => {
        if (position === index) {
            section.link.setAttribute("aria-current", "true");
        } else {
            section.link.removeAttribute("aria-current");
        }
    });
    const number = index + 1;
    navigator.current.textContent = sections[index].heading.textContent.trim();
    navigator.pinnedText.textContent = navigator.current.textContent;
    navigator.pinned.href = sections[index].link.getAttribute("href");
    navigator.title.textContent = `On this page · ${number} / ${navigator.total}`;
    navigator.ring.style.setProperty("--progress", String(number / navigator.total));
    navigator.toggle.setAttribute(
        "aria-label",
        `On this page: section ${number} of ${navigator.total}, ${navigator.current.textContent}`,
    );
}

/** SVG-иконка из готового пути (разметка — константа модуля, не данные со страницы). */
function icon(paths, size) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const template = document.createElementNS("http://www.w3.org/2000/svg", "g");
    template.innerHTML = paths;
    svg.append(...template.childNodes);
    return svg;
}

function element(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
}
